'use client'

import { useCallback, useRef } from 'react'
import { toast } from 'sonner'
import {
  getPasteHandler,
  isAmbiguousPaste,
  parseClipboardText,
} from '../registry/block-paste-registry'
import { showPasteSuggestion } from '../editor/PasteSuggestion'
import { useNotebookStore } from './store'
import { getRowFocusMetaFromDom } from '../models/semantic-row-paste'
import { shouldOfferBlockSemanticPaste } from './paste-target'

const PASTE_DEBOUNCE_MS = 300
const pasteTimestamps = new Map()

function notifyCap(result) {
  if (result?.pasteWasCapped) {
    toast.info('Paste limited to 100 items', { duration: 3000 })
  }
}

/**
 * Capture-phase paste on block container — runs before the editor, so structured
 * clipboard content can become rows/items while multiline body fields keep inline paste.
 */
export function usePaste(blockType, blockId, data, onChange, enabled = true) {
  const dataRef = useRef(data)
  dataRef.current = data

  const commitPaste = useCallback(
    (parsed, event) => {
      const handler = getPasteHandler(blockType)
      if (!handler?.canHandlePaste(parsed)) return false

      const current = dataRef.current ?? {}
      const focus = getRowFocusMetaFromDom(event)
      const next = handler.transformPaste(parsed, current, focus)
      useNotebookStore.getState().pushUndoSnapshot(blockId, current)
      onChange?.(next)
      notifyCap(next)
      return true
    },
    [blockType, blockId, onChange],
  )

  const onPasteCapture = useCallback(
    (e) => {
      if (!enabled || !onChange || !blockId) return
      if (!shouldOfferBlockSemanticPaste(e.target)) return

      const now = Date.now()
      const last = pasteTimestamps.get(blockId) ?? 0
      if (now - last < PASTE_DEBOUNCE_MS) return

      const raw = e.clipboardData?.getData('text/plain') || ''
      if (!raw.trim()) return

      const parsed = parseClipboardText(raw)
      const handler = getPasteHandler(blockType)
      if (!handler || !handler.canHandlePaste(parsed)) return

      e.preventDefault()
      e.stopPropagation()

      if (isAmbiguousPaste(blockType, parsed)) {
        pasteTimestamps.set(blockId, now)
        showPasteSuggestion({
          onConvert: () => commitPaste(parsed, e),
          onKeep: () => {},
        })
        return
      }

      pasteTimestamps.set(blockId, now)
      commitPaste(parsed, e)
    },
    [blockType, blockId, enabled, onChange, commitPaste],
  )

  return { onPasteCapture }
}

export function blockHasSemanticPaste(blockType) {
  return Boolean(getPasteHandler(blockType))
}
